import React, { useState, useRef, useEffect } from 'react';
import MediaLibrary, { MediaImage } from './MediaLibrary';
import ImageCropper from './ImageCropper';
import AdminHeader from './AdminHeader';
import PageHeader from './PageHeader';
import './BlogManager.scss';

import EditorJS from '@editorjs/editorjs';
import Header from '@editorjs/header';
import List from '@editorjs/list';
import ImageTool from '@editorjs/image';
import Embed from '@editorjs/embed';
import Table from '@editorjs/table';
import CodeTool from '@editorjs/code';

interface Category {
  id: number;
  name: string;
  createdAt: string;
  updatedAt: string;
}

interface Blog {
  id: number;
  title: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  tags: string;
  excerpt: string;
  content: string;
  featuredImage: string;
  featuredImageAlt: string;
  author: string;
  date: string;
  views: number;
  likes: number;
  comments: number;
  categoryIds?: number[];
  Categories?: Category[];
}
// Mock blog data - will be replaced with API calls
const initialBlogs: Blog[] = [
  {
    id: 1,
    title: 'First Blog Post',
    slug: 'first-blog-post',
    metaTitle: 'First Blog Post - SEO Title',
    metaDescription: 'This is the meta description for the first blog post.',
    tags: 'react, development, web',
    excerpt: 'Short excerpt of the first blog post.',
    content: 'This is the first blog post.',
    featuredImage: '',
    featuredImageAlt: '',
    author: 'Admin',
    date: '2025-10-10',
    views: 120,
    likes: 15,
    comments: 3,
    categoryIds: [1, 2],
  },
  {
    id: 2,
    title: 'Second Blog Post',
    slug: 'second-blog-post',
    metaTitle: 'Second Blog Post - SEO Title',
    metaDescription: 'This is the meta description for the second blog post.',
    tags: 'javascript, tutorial, guide',
    excerpt: 'Short excerpt of the second blog post.',
    content: 'This is the second blog post.',
    featuredImage: '',
    featuredImageAlt: '',
    author: 'Admin',
    date: '2025-10-12',
    views: 80,
    likes: 8,
    comments: 1,
    categoryIds: [1],
  },
];

const BlogManager: React.FC = () => {
  const [blogs, setBlogs] = useState<Blog[]>(initialBlogs);
  const [categories, setCategories] = useState<Category[]>([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editBlog, setEditBlog] = useState<Blog | null>(null);

  // Format date to Indian Standard Time (IST)
  const formatIndianDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    
    const date = new Date(dateString);
    
    // Format to Indian date and time
    const options: Intl.DateTimeFormatOptions = {
      timeZone: 'Asia/Kolkata',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    };
    
    return date.toLocaleString('en-IN', options);
  };
  const [form, setForm] = useState({
    title: '',
    slug: '',
    metaTitle: '',
    metaDescription: '',
    tags: '',
    categories: [] as number[],
    author: '',
    date: '',
    excerpt: '',
    featuredImage: '',
    featuredImageAlt: '',
    content: '',
  });
  const [featuredImagePreview, setFeaturedImagePreview] = useState<string | null>(null);
  const [showMediaLibrary, setShowMediaLibrary] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropImage, setCropImage] = useState<string | null>(null);
  const [mediaImages, setMediaImages] = useState<MediaImage[]>([]);
  const [isOnline, setIsOnline] = useState(true);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewBlog, setViewBlog] = useState<Blog | null>(null);
  const editorRef = useRef<any>(null);
  // Use a string id for Editor.js holder to ensure proper mounting
  const EDITOR_HOLDER_ID = 'blog-editorjs-holder';

  const API_BASE = 'http://localhost:4000/api';

  // Fetch categories from backend
  const fetchCategories = async () => {
    try {
      const response = await fetch(`${API_BASE}/categories`);
      if (!response.ok) throw new Error('Failed to fetch categories');
      const data = await response.json();
      setCategories(data);
      setIsOnline(true);
    } catch (err) {
      console.error('Fetch categories error:', err);
      setIsOnline(false);
      // Use mock data for demo if backend is not available
      setCategories([
        { id: 1, name: 'Web Development', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
        { id: 2, name: 'Mobile Apps', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
        { id: 3, name: 'SEO & Marketing', createdAt: '2025-10-15', updatedAt: '2025-10-15' },
      ]);
    }
  };

  // Fetch blogs from backend
  const fetchBlogs = async () => {
    try {
      const response = await fetch(`${API_BASE}/blogs`);
      if (!response.ok) throw new Error('Failed to fetch blogs');
      const data = await response.json();
      setBlogs(data);
      setIsOnline(true);
    } catch (err) {
      console.error('Fetch blogs error:', err);
      setIsOnline(false);
      // Keep using initialBlogs mock data
    }
  };

  // Save blog to backend
  const saveBlogToBackend = async (blogData: any) => {
    try {
      const method = editBlog ? 'PUT' : 'POST';
      const url = editBlog ? `${API_BASE}/blogs/${editBlog.id}` : `${API_BASE}/blogs`;
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...blogData,
          categoryIds: form.categories,
        }),
      });
      
      if (!response.ok) throw new Error('Failed to save blog');
      const savedBlog = await response.json();
      
      if (editBlog) {
        setBlogs(blogs.map(b => b.id === editBlog.id ? savedBlog : b));
      } else {
        setBlogs([...blogs, savedBlog]);
      }
      setIsOnline(true);
    } catch (err) {
      console.error('Save blog error:', err);
      setIsOnline(false);
      // Fallback to local state management for demo
      const newBlog = {
        ...blogData,
        categoryIds: form.categories,
        Categories: categories.filter(cat => form.categories.includes(cat.id)),
        id: editBlog ? editBlog.id : Date.now(),
        views: editBlog ? editBlog.views : 0,
        likes: editBlog ? editBlog.likes : 0,
        comments: editBlog ? editBlog.comments : 0,
      };
      
      if (editBlog) {
        setBlogs(blogs.map(b => b.id === editBlog.id ? newBlog : b));
      } else {
        setBlogs([...blogs, newBlog]);
      }
    }
  };

  // Editor.js is a free, modern content editor
  // For demo, use a textarea. You can integrate Editor.js or another editor later.

  // Handle featured image upload
  // Handle featured image upload with cropping
  const handleFeaturedImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropImage(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle crop complete
  const handleCropComplete = (croppedDataUrl: string) => {
    setFeaturedImagePreview(croppedDataUrl);
    setForm(f => ({ ...f, featuredImage: croppedDataUrl }));
    setShowCropper(false);
    setCropImage(null);
  };

  // Media library logic
  const handleMediaLibrarySelect = (img: MediaImage) => {
    setFeaturedImagePreview(img.url);
    setForm(f => ({ ...f, featuredImage: img.url, featuredImageAlt: img.alt }));
    setShowMediaLibrary(false);
  };
  const handleMediaLibraryUpload = (file: File) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const url = reader.result as string;
      const newImg = { url, alt: '' };
      setMediaImages(imgs => [...imgs, newImg]);
    };
    reader.readAsDataURL(file);
  };

  const openEditor = (blog?: any) => {
    if (blog) {
      setEditBlog(blog);
      setForm({
        title: blog.title || '',
        slug: blog.slug || '',
        metaTitle: blog.metaTitle || '',
        metaDescription: blog.metaDescription || '',
        tags: blog.tags || '',
        categories: blog.categoryIds || [],
        author: blog.author || '',
        date: blog.date || '',
        excerpt: blog.excerpt || '',
        featuredImage: blog.featuredImage || '',
        featuredImageAlt: blog.featuredImageAlt || '',
        content: blog.content || '',
      });
      setFeaturedImagePreview(blog.featuredImage || null);
    } else {
      setEditBlog(null);
      setForm({
        title: '',
        slug: '',
        metaTitle: '',
        metaDescription: '',
        tags: '',
        categories: [],
        author: '',
        date: new Date().toISOString().slice(0, 10),
        excerpt: '',
        featuredImage: '',
        featuredImageAlt: '',
        content: '',
      });
      setFeaturedImagePreview(null);
    }
    setShowEditor(true);
  };

  const openViewModal = (blog: any) => {
    setViewBlog(blog);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setViewBlog(null);
  };

  // Render Editor.js content as HTML
  const renderEditorContent = (content: string) => {
    try {
      const parsed = JSON.parse(content);
      if (!parsed.blocks) return <p>No content available</p>;

      return parsed.blocks.map((block: any, index: number) => {
        switch (block.type) {
          case 'header':
            const level = block.data.level;
            if (level === 1) return <h1 key={index}>{block.data.text}</h1>;
            if (level === 2) return <h2 key={index}>{block.data.text}</h2>;
            if (level === 3) return <h3 key={index}>{block.data.text}</h3>;
            if (level === 4) return <h4 key={index}>{block.data.text}</h4>;
            if (level === 5) return <h5 key={index}>{block.data.text}</h5>;
            return <h6 key={index}>{block.data.text}</h6>;
          
          case 'paragraph':
            return <p key={index} dangerouslySetInnerHTML={{ __html: block.data.text }} />;
          
          case 'list':
            const ListTag = block.data.style === 'ordered' ? 'ol' : 'ul';
            return (
              <ListTag key={index}>
                {block.data.items.map((item: string, i: number) => (
                  <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
                ))}
              </ListTag>
            );
          
          case 'code':
            return <pre key={index}><code>{block.data.code}</code></pre>;
          
          case 'image':
            return (
              <div key={index} className="blog-manager__view-image-block">
                <img src={block.data.file.url} alt={block.data.caption || ''} />
                {block.data.caption && <p className="caption">{block.data.caption}</p>}
              </div>
            );
          
          default:
            return <div key={index}>{JSON.stringify(block.data)}</div>;
        }
      });
    } catch (error) {
      console.error('Error rendering content:', error);
      return <p>Error loading content</p>;
    }
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditBlog(null);
    setForm({
      title: '',
      slug: '',
      metaTitle: '',
      metaDescription: '',
      tags: '',
      categories: [],
      author: '',
      date: new Date().toISOString().slice(0, 10),
      excerpt: '',
      featuredImage: '',
      featuredImageAlt: '',
      content: '',
    });
    if (editorRef.current) {
      editorRef.current.destroy();
      editorRef.current = null;
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleCategoryChange = (categoryId: number) => {
    const currentCategories = form.categories;
    if (currentCategories.includes(categoryId)) {
      setForm({ ...form, categories: currentCategories.filter(id => id !== categoryId) });
    } else {
      setForm({ ...form, categories: [...currentCategories, categoryId] });
    }
  };

  const handleSave = async () => {
    if (!editorRef.current) return;
    const output = await editorRef.current.save();
    const content = JSON.stringify(output);
    const blogData = {
      ...form,
      content,
      date: form.date || new Date().toISOString().slice(0, 10),
    };
    
    await saveBlogToBackend(blogData);
    closeEditor();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this blog post? This action cannot be undone.')) {
      try {
        const response = await fetch(`${API_BASE}/blogs/${id}`, {
          method: 'DELETE',
        });
        
        if (response.ok) {
          // Remove from state after successful deletion
          setBlogs(blogs.filter(b => b.id !== id));
          alert('Blog post deleted successfully!');
        } else {
          alert('Failed to delete blog post. Please try again.');
        }
      } catch (error) {
        console.error('Delete error:', error);
        alert('Error deleting blog post. Please try again.');
      }
    }
  };

  // Initialize Editor.js and lock body scroll when modal is open
  useEffect(() => {
    if (showEditor) {
      // Lock body scroll
      document.body.style.overflow = 'hidden';
      // Wait for modal to render
      setTimeout(() => {
        if (editorRef.current) {
          editorRef.current.destroy();
          editorRef.current = null;
        }
        editorRef.current = new EditorJS({
          holder: EDITOR_HOLDER_ID,
          data: editBlog && editBlog.content ? JSON.parse(editBlog.content) : undefined,
          placeholder: 'Write your blog content here...',
          minHeight: 200,
          tools: {
            header: Header,
            list: List,
            image: {
              class: ImageTool,
              config: {
                uploader: {
                  uploadByFile: (file: File) => {
                    return new Promise((resolve) => {
                      const reader = new FileReader();
                      reader.onload = () => {
                        resolve({
                          success: 1,
                          file: {
                            url: reader.result as string,
                          }
                        });
                      };
                      reader.readAsDataURL(file);
                    });
                  }
                }
              }
            },
            embed: Embed as any,
            table: Table,
            code: CodeTool,
          },
        });
      }, 0);
    } else {
      // Unlock body scroll
      document.body.style.overflow = '';
    }
    // Cleanup on close
    return () => {
      document.body.style.overflow = '';
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
    };
    // eslint-disable-next-line
  }, [showEditor]);

  // Fetch data on component mount
  useEffect(() => {
    fetchCategories();
    fetchBlogs();
  }, []);

  return (
    <div className="admin-dashboard">
      <AdminHeader />
      <div className="blog-manager">
        <PageHeader 
          title="Blog Management" 
          subtitle={!isOnline ? "⚠️ Offline Mode (Demo Data)" : "Manage your blog posts and content"}
        />
        <div className="blog-manager__header">
          <div>
            <span className="blog-manager__title">Blog Posts</span>
          </div>
          <button className="blog-manager__add-btn" onClick={() => openEditor()}>Add New Post</button>
        </div>
        <div className="blog-manager__table-container">
          <table className="blog-manager__table">
            <thead>
              <tr>
                <th>Title</th>
                <th>Categories</th>
                <th>Date</th>
                <th>Views</th>
                <th>Likes</th>
                <th>Comments</th>
                <th>Actions</th>
              </tr>
            </thead>
          <tbody>
            {blogs.map(blog => (
              <tr key={blog.id}>
                <td>{blog.title}</td>
                <td>
                  {blog.Categories?.map(cat => cat.name).join(', ') || 
                   categories.filter(cat => blog.categoryIds?.includes(cat.id)).map(cat => cat.name).join(', ') ||
                   'No categories'}
                </td>
                <td>{formatIndianDate(blog.date)}</td>
                <td>{blog.views}</td>
                <td>{blog.likes}</td>
                <td>{blog.comments}</td>
                <td className="blog-manager__actions">
                  <div className="blog-manager__action-buttons">
                    <button 
                      className="blog-manager__btn blog-manager__btn--view" 
                      onClick={() => openViewModal(blog)}
                      title="View full post"
                    >
                      View
                    </button>
                    <button 
                      className="blog-manager__btn blog-manager__btn--edit" 
                      onClick={() => openEditor(blog)}
                      title="Edit post"
                    >
                      Edit
                    </button>
                    <button 
                      className="blog-manager__btn blog-manager__btn--delete" 
                      onClick={() => handleDelete(blog.id)}
                      title="Delete post"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>

        {/* Editor Modal */}
        {showEditor && (
          <div className="blog-manager__editor-modal">
            <div className="blog-manager__editor-content">
              <h3>{editBlog ? 'Edit Post' : 'Add New Post'}</h3>
              <div className="blog-manager__form-row">
                <label>
                  Title
                  <input
                    type="text"
                    name="title"
                    value={form.title}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="Enter blog title"
                  />
                </label>
                <label>
                  Slug
                  <input
                    type="text"
                    name="slug"
                    value={form.slug}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="auto-generated or custom-slug"
                  />
                </label>
              </div>
              <div className="blog-manager__form-row">
                <label>
                  Meta Title
                  <input
                    type="text"
                    name="metaTitle"
                    value={form.metaTitle}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="SEO meta title"
                  />
                </label>
                <label>
                  Meta Description
                  <input
                    type="text"
                    name="metaDescription"
                    value={form.metaDescription}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="SEO meta description"
                  />
                </label>
              </div>
              <div className="blog-manager__form-row">
                <label>
                  Tags (comma separated)
                  <input
                    type="text"
                    name="tags"
                    value={form.tags}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="e.g. react, webdev, seo"
                  />
                </label>
                <label>
                  Categories
                  <div className="blog-manager__categories">
                    {categories.map(category => (
                      <label key={category.id} className="blog-manager__category-checkbox">
                        <input
                          type="checkbox"
                          checked={form.categories.includes(category.id)}
                          onChange={() => handleCategoryChange(category.id)}
                        />
                        <span>{category.name}</span>
                      </label>
                    ))}
                    {categories.length === 0 && (
                      <p className="blog-manager__no-categories">
                        No categories available. <a href="/admin/categories">Create categories first</a>.
                      </p>
                    )}
                  </div>
                </label>
              </div>
              <div className="blog-manager__form-row">
                <label>
                  Author Name
                  <input
                    type="text"
                    name="author"
                    value={form.author}
                    onChange={handleChange}
                    className="blog-manager__input"
                    placeholder="Author name"
                  />
                </label>
                <label>
                  Date
                  <input
                    type="date"
                    name="date"
                    value={form.date}
                    onChange={handleChange}
                    className="blog-manager__input"
                  />
                </label>
              </div>
              <div className="blog-manager__form-block">
                <label className="blog-manager__label">
                  Excerpt
                  <textarea
                    name="excerpt"
                    value={form.excerpt}
                    onChange={handleChange}
                    className="blog-manager__input blog-manager__input--textarea"
                    placeholder="Short summary for SEO and previews"
                    rows={2}
                  />
                </label>
              </div>
              <div className="blog-manager__form-block blog-manager__featured-image-block">
                <div className="blog-manager__featured-image-label">Featured Image</div>
                {featuredImagePreview && (
                  <img
                    src={featuredImagePreview}
                    alt={form.featuredImageAlt || 'Featured'}
                    className="blog-manager__featured-image-preview"
                  />
                )}
                <div className="blog-manager__featured-image-actions">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleFeaturedImageChange}
                    className="blog-manager__input"
                  />
                  <button type="button" className="blog-manager__btn blog-manager__btn--media" onClick={() => setShowMediaLibrary(true)}>
                    Media Library
                  </button>
                </div>
                <input
                  type="text"
                  name="featuredImageAlt"
                  value={form.featuredImageAlt}
                  onChange={handleChange}
                  className="blog-manager__input"
                  placeholder="Alt text for accessibility and SEO"
                />
              </div>
              <div>
                <label>
                  Content
                  <div id={EDITOR_HOLDER_ID} className="blog-manager__editorjs-container" />
                  <p className="blog-manager__editor-help">
                    💡 <strong>Tip:</strong> Use the image tool (📷) in the editor toolbar. You can paste image URLs directly, or upload images by dragging and dropping.
                  </p>
                </label>
              </div>
              <div className="blog-manager__editor-actions">
                <button className="blog-manager__btn blog-manager__btn--cancel" onClick={closeEditor}>Cancel</button>
                <button className="blog-manager__btn blog-manager__btn--primary" onClick={handleSave}>
                  {editBlog ? 'Update' : 'Create'}
                </button>
              </div>
            </div>
          </div>
        )}
      {/* Media Library Modal */}
      {showMediaLibrary && (
        <MediaLibrary
          images={mediaImages}
          onSelect={handleMediaLibrarySelect}
          onUpload={handleMediaLibraryUpload}
          onClose={() => setShowMediaLibrary(false)}
        />
      )}
      {/* Image Cropper Modal */}
      {showCropper && cropImage && (
        <ImageCropper
          image={cropImage}
          onCropComplete={handleCropComplete}
          onCancel={() => { setShowCropper(false); setCropImage(null); }}
        />
      )}

      {/* View Blog Modal */}
      {showViewModal && viewBlog && (
        <div className="blog-manager__modal-overlay" onClick={closeViewModal}>
          <div className="blog-manager__view-modal" onClick={(e) => e.stopPropagation()}>
            <div className="blog-manager__view-header">
              <h2>📖 {viewBlog.title}</h2>
              <button 
                className="blog-manager__close-btn" 
                onClick={closeViewModal}
                title="Close"
              >
                ✕
              </button>
            </div>
            
            <div className="blog-manager__view-content">
              <div className="blog-manager__view-meta">
                <div className="blog-manager__view-meta-row">
                  <span><strong>Author:</strong> {viewBlog.author || 'Unknown'}</span>
                  <span><strong>Date:</strong> {formatIndianDate(viewBlog.date)}</span>
                </div>
                <div className="blog-manager__view-meta-row">
                  <span><strong>Views:</strong> {viewBlog.views}</span>
                  <span><strong>Likes:</strong> {viewBlog.likes}</span>
                  <span><strong>Comments:</strong> {viewBlog.comments}</span>
                </div>
                <div className="blog-manager__view-meta-row">
                  <span><strong>Slug:</strong> {viewBlog.slug}</span>
                </div>
                {viewBlog.tags && (
                  <div className="blog-manager__view-meta-row">
                    <span><strong>Tags:</strong> {viewBlog.tags}</span>
                  </div>
                )}
                {viewBlog.Categories && viewBlog.Categories.length > 0 && (
                  <div className="blog-manager__view-meta-row">
                    <span><strong>Categories:</strong> {viewBlog.Categories.map((cat: any) => cat.name).join(', ')}</span>
                  </div>
                )}
              </div>

              {viewBlog.featuredImage && (
                <div className="blog-manager__view-featured-image">
                  <img 
                    src={viewBlog.featuredImage} 
                    alt={viewBlog.featuredImageAlt || viewBlog.title}
                    className="blog-manager__view-image"
                  />
                </div>
              )}

              {viewBlog.excerpt && (
                <div className="blog-manager__view-excerpt">
                  <h3>Excerpt</h3>
                  <p>{viewBlog.excerpt}</p>
                </div>
              )}

              <div className="blog-manager__view-seo">
                <h3>SEO Information</h3>
                <div className="blog-manager__view-seo-item">
                  <strong>Meta Title:</strong> {viewBlog.metaTitle || 'Not set'}
                </div>
                <div className="blog-manager__view-seo-item">
                  <strong>Meta Description:</strong> {viewBlog.metaDescription || 'Not set'}
                </div>
              </div>

              <div className="blog-manager__view-content-section">
                <h3>Content Preview</h3>
                <div className="blog-manager__view-content-preview">
                  {viewBlog.content ? (
                    <div className="blog-manager__editor-preview">
                      {renderEditorContent(viewBlog.content)}
                    </div>
                  ) : (
                    <p>No content available</p>
                  )}
                </div>
              </div>
            </div>

            <div className="blog-manager__view-actions">
              <button 
                className="blog-manager__btn blog-manager__btn--edit" 
                onClick={() => {
                  closeViewModal();
                  openEditor(viewBlog);
                }}
              >
                ✏️ Edit This Post
              </button>
              <button 
                className="blog-manager__btn blog-manager__btn--secondary" 
                onClick={closeViewModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
};

export default BlogManager;

